import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { getCourses, createCourse as createCourseApi, deleteCourse } from "../services/courses";
import { getToken } from "../services/auth";
import { jwtDecode } from "jwt-decode";
import Sidebar from "../components/Sidebar";
import { Plus, Video, Trash2, Edit3, PlayCircle, Layers3, Target, Gamepad2, MessageSquare, ClipboardCheck, BookOpen } from "lucide-react";
import toast, { Toaster } from 'react-hot-toast';

// New UI Components
import { Button, Card, Badge, StatCard, Skeleton } from "../components/ui";
import ChartWidget from "../components/advanced/ChartWidget";
import AnimatedDataTable from "../components/advanced/AnimatedDataTable";

export default function TeacherDashboard() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ courses: 0, lessons: 0, live_classes: 0, exams: 0, doubts: 0, assignments: 0 });
  
  const [title, setTitle] = useState("");
  const [courseCategory, setCourseCategory] = useState("");
  const [coursePrice, setCoursePrice] = useState(0);
  const [originalPrice, setOriginalPrice] = useState(0);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [editingCourseId, setEditingCourseId] = useState(null);

  useEffect(() => {
    const init = async () => {
      const token = getToken();
      if (!token) return;
      const user = jwtDecode(token);

      try {
        const [res, statsRes] = await Promise.all([
          getCourses(),
          api.get("/courses/teacher/stats").catch(() => ({ data: { courses: 0, lessons: 0, live_classes: 0, exams: 0, doubts: 0, assignments: 0 } }))
        ]);
        if (statsRes && statsRes.data) setStats(statsRes.data);
        const myCourses = res.data;
        setCourses(myCourses);
      } catch (err) {
        toast.error("Failed to load dashboard");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const handleSaveCourse = async () => {
    if (!title) return toast.error("Enter course title");
    setIsUploading(true);
    let thumbnailUrl = "";
    
    try {
      if (thumbnailFile) {
        const formData = new FormData();
        formData.append("file", thumbnailFile);
        const uploadRes = await api.post("/upload/file", formData);
        thumbnailUrl = uploadRes.data.url;
      }

      const payload = { 
        title, 
        description: "Course", 
        price: coursePrice, 
        original_price: originalPrice, 
        category: courseCategory,
      };
      if (thumbnailUrl) payload.thumbnail = thumbnailUrl;

      if (editingCourseId) {
        await api.put(`/courses/${editingCourseId}`, payload);
        toast.success("Course updated!");
      } else {
        await createCourseApi(payload);
        toast.success("Course created!");
      }
      
      handleCancelEdit();
      
      const coursesRes = await getCourses();
      setCourses(coursesRes.data); // Remove strict filtering so institute courses show up
    } catch (err) {
      toast.error(editingCourseId ? "Failed to update course" : "Failed to create course");
    } finally {
      setIsUploading(false);
    }
  };

  const handleEditClick = (course) => {
    setEditingCourseId(course.id);
    setTitle(course.title || "");
    setCourseCategory(course.category || "");
    setCoursePrice(course.price || 0);
    setOriginalPrice(course.original_price || 0);
    setThumbnailFile(null);
  };

  const handleCancelEdit = () => {
    setEditingCourseId(null);
    setTitle(""); 
    setCourseCategory(""); 
    setCoursePrice(0); 
    setOriginalPrice(0); 
    setThumbnailFile(null);
  };

  const handleDeleteCourse = async (courseId) => {
    if (!window.confirm("Are you sure you want to delete this course?")) return;
    try {
      await deleteCourse(courseId);
      setCourses(courses.filter(c => c.id !== courseId));
      toast.success("Course deleted!");
    } catch (err) {
      toast.error("Failed to delete course");
    }
  };

  // Convert stats to chart data
  const chartData = [
    { name: "Courses", value: stats.courses },
    { name: "Classes", value: stats.lessons },
    { name: "Live", value: stats.live_classes },
    { name: "Exams", value: stats.exams },
    { name: "Doubts", value: stats.doubts },
    { name: "Tasks", value: stats.assignments },
  ];

  const courseColumns = [
    { header: "Course Title", accessor: "title", render: (val) => <span className="font-bold text-slate-900 dark:text-white">{val}</span> },
    { header: "Category", accessor: "category", render: (val) => <Badge variant="primary">{val || "Uncategorized"}</Badge> },
    { header: "Price", accessor: "price", render: (val) => <span className="font-semibold text-emerald-600">₹{val}</span> },
    { 
      header: "Actions", 
      accessor: "id", 
      sortable: false,
      render: (id, row) => (
        <div className="flex items-center gap-2">
          <Button variant="primary" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/teacher-course/${id}`); }}>
            Manage Content
          </Button>
          <Button variant="ghost" size="sm" leftIcon={<Edit3 size={14}/>} onClick={(e) => { e.stopPropagation(); handleEditClick(row); }}>
            Edit
          </Button>
          <Button variant="danger" size="sm" leftIcon={<Trash2 size={14}/>} onClick={(e) => { e.stopPropagation(); handleDeleteCourse(id); }}>
            Delete
          </Button>
        </div>
      ) 
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex font-['Inter',sans-serif]">
      <Toaster position="top-center" />
      <Sidebar role="teacher" />

      <main className="flex-1 lg:ml-64 p-6 lg:p-10 space-y-8">
        <header>
          <h1 className="text-3xl font-black tracking-tight text-slate-900 dark:text-white">Teacher Workspace</h1>
          <p className="text-slate-500 mt-1">Manage your courses, monitor engagement, and track your revenue.</p>
        </header>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4">
            {Array(6).fill(0).map((_, i) => <Skeleton.Card key={i} />)}
          </div>
        ) : (
          <section className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
            <StatCard title="Total Courses" value={stats.courses} icon={Layers3} variant="gradient" gradient="from-indigo-600 to-violet-600" />
            <StatCard title="Recorded Classes" value={stats.lessons} icon={PlayCircle} variant="glass" />
            <StatCard title="Live Sessions" value={stats.live_classes} icon={Video} variant="elevated" />
            <StatCard title="Quizzes Built" value={stats.exams} icon={ClipboardCheck} variant="default" />
            <StatCard title="Doubts Resolved" value={stats.doubts} icon={MessageSquare} variant="glass" />
            <StatCard title="Assignments" value={stats.assignments} icon={Target} variant="default" />
          </section>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            
            {/* My Courses Data Table */}
            <section>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold flex items-center gap-2 text-slate-900 dark:text-white">
                  <BookOpen className="text-indigo-500" /> Managed Courses
                </h3>
              </div>
              <AnimatedDataTable 
                data={courses}
                columns={courseColumns}
                isLoading={loading}
                pageSize={5}
                searchPlaceholder="Search courses..."
              />
            </section>

            {/* Course Creation Form using new UI elements */}
            <Card variant="gradient-border" className="p-6">
              <Card.Header title={editingCourseId ? "Edit Course" : "Publish New Course"} subtitle={editingCourseId ? "Update your course details." : "Tag courses by category, add pricing, and publish instantly."} />
              <Card.Body className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-medium dark:text-white"
                    placeholder="Course Title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                  <input
                    className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-medium dark:text-white"
                    placeholder="Category"
                    value={courseCategory}
                    onChange={(e) => setCourseCategory(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <input
                    type="number"
                    className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-medium dark:text-white"
                    placeholder="Discounted Price (₹)"
                    value={coursePrice}
                    onChange={(e) => setCoursePrice(parseFloat(e.target.value))}
                  />
                  <input
                    type="number"
                    className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 transition-all font-medium dark:text-white"
                    placeholder="Original Price (₹)"
                    value={originalPrice}
                    onChange={(e) => setOriginalPrice(parseFloat(e.target.value))}
                  />
                </div>
                
                <div className="w-full">
                  <label className="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
                    Course Thumbnail (Optional)
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl font-medium dark:text-white file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                    onChange={(e) => setThumbnailFile(e.target.files[0])}
                  />
                </div>

                <div className="flex gap-4 mt-4">
                  <Button variant="primary" className="flex-1" onClick={handleSaveCourse} disabled={isUploading} icon={<Plus size={18}/>}>
                    {isUploading ? "Saving..." : (editingCourseId ? "Update Course" : "Create Course")}
                  </Button>
                  {editingCourseId && (
                    <Button variant="secondary" onClick={handleCancelEdit} disabled={isUploading}>
                      Cancel
                    </Button>
                  )}
                </div>
              </Card.Body>
            </Card>

          </div>

          <div className="space-y-8">
            {/* Activity Chart */}
            <ChartWidget 
              title="Portfolio Distribution" 
              subtitle="Breakdown of your created content"
              data={chartData}
              height={250}
            />

            {/* Gamification Panel */}
            <Card variant="glass" className="overflow-hidden relative bg-gradient-to-br from-indigo-900 to-violet-900 border-none text-white">
              <div className="absolute right-0 top-0 h-32 w-32 translate-x-10 -translate-y-10 rounded-full bg-white/10 blur-3xl" />
              <Card.Body className="relative z-10 p-6 flex flex-col items-start gap-4">
                <Badge variant="success" className="bg-white/20 text-white border-none">Gamification</Badge>
                <div>
                  <h3 className="text-xl font-black flex items-center gap-2">
                    <Gamepad2 size={22} className="text-amber-300" /> Interactive Quizzes
                  </h3>
                  <p className="mt-2 text-sm text-indigo-100 opacity-90 leading-relaxed">
                    Create playful quiz sessions that reward stars and badges for young learners.
                  </p>
                </div>
                <Button variant="secondary" className="mt-2" onClick={() => navigate("/teacher-exams")}>
                  Build a Quiz
                </Button>
              </Card.Body>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
